import type { FileUrlDescriptor } from '@magicdoorinc/file';
import {
  Amenity,
  AmenityAvalability,
  AmenityType,
  CompanyUnit,
  EmbeddedFile,
  LeaseTerm,
  PaginatedUnits,
  PetPolicy,
  PetSize,
  PropertyType,
  TenantUnitListing,
  UnitDescriptionTranslation,
  UnitParams,
} from '~/use-cases/types/Unit';
import { Gateway } from './base/gateway';
import { fileGateway } from './fileGateway';
import { fullStackNetworkManager } from './network/fullStackNetworkManager';
import { MagicRequest, RequestMethod } from './network/magicRequest';

const COMPANY_UNITS_LISTING_URL = '/api/unit-listings';

interface EmbeddedFileJson {
  fileId?: string;
  description?: string;
  default?: boolean;
  fileUrl?: FileUrlDescriptor;
}

interface UnitListingJson {
  id?: string;
  rentalPrice?: number;
  availableFrom?: string;
  leaseTerm?: string;
  title?: string;
  listed?: string;
  pinned?: boolean;
  primaryImage?: EmbeddedFileJson;
  primaryVideo?: EmbeddedFileJson;
  propertyType?: string;
  beds?: number;
  baths?: number;
  unitSizeSqft?: number;
  singleLineAddress?: string;
}

interface PaginatedUnitListingJson {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  items?: UnitListingJson[];
}

interface AmenityJson {
  type?: string;
  name?: string;
  icon?: string;
  avalability?: string;
  description?: string;
}

interface PetPolicyJson {
  allowed?: boolean;
  policies?: PetPolicy['allowedPetTypes'];
  size?: string;
  fee?: number;
  deposit?: number;
  description?: string;
}

interface UnitListingDetailJson extends UnitListingJson {
  securityDeposit?: number;
  description?: string;
  descriptionTranslations?: UnitDescriptionTranslation[];
  images?: EmbeddedFileJson[];
  videos?: EmbeddedFileJson[];
  tags?: string[];
  petPolicy?: PetPolicyJson;
  amenities?: AmenityJson[];
  locationAnalysis?: string;
  terms?: Record<string, string>;
  updated?: string;
}

const oneOf =
  <T extends string>(values: Record<string, T>) =>
  (raw?: string): T | undefined =>
    (Object.values(values) as string[]).includes(raw ?? '') ? (raw as T) : undefined;

const toLeaseTerm = oneOf(LeaseTerm);
const toAmenityType = oneOf(AmenityType);
const toAmenityAvalability = oneOf(AmenityAvalability);
const toPetSize = oneOf(PetSize);
const toPropertyType = oneOf(PropertyType);

const toDate = (value?: string): Date | undefined => (value ? new Date(value) : undefined);

const toEmbeddedFile = (json: EmbeddedFileJson): EmbeddedFile => ({
  default: json.default,
  description: json.description,
  fileId: json.fileId,
  fileUrl: json.fileUrl,
  urls: fileGateway.fileUrlsOf(json.fileUrl),
});

const toCompanyUnit = (json: UnitListingJson): CompanyUnit => ({
  availableFrom: toDate(json.availableFrom),
  baths: json.baths,
  beds: json.beds,
  id: json.id,
  leaseTerm: toLeaseTerm(json.leaseTerm),
  listed: toDate(json.listed),
  pinned: json.pinned,
  primaryImage: json.primaryImage && toEmbeddedFile(json.primaryImage),
  primaryVideo: json.primaryVideo && toEmbeddedFile(json.primaryVideo),
  propertyType: toPropertyType(json.propertyType),
  rentalPrice: json.rentalPrice,
  singleLineAddress: json.singleLineAddress,
  title: json.title,
  unitSizeSqft: json.unitSizeSqft,
});

const toAmenity = (json: AmenityJson): Amenity => ({
  avalability: toAmenityAvalability(json.avalability),
  description: json.description,
  icon: json.icon,
  name: json.name,
  type: toAmenityType(json.type),
});

const toPetPolicy = (json: PetPolicyJson): PetPolicy => ({
  allowed: json.allowed,
  allowedPetTypes: json.policies,
  size: toPetSize(json.size),
  fee: json.fee,
  deposit: json.deposit,
  description: json.description,
});

const toUnitListing = (json: UnitListingDetailJson): TenantUnitListing => ({
  amenities: json.amenities?.map(toAmenity),
  availableFrom: toDate(json.availableFrom),
  baths: json.baths,
  beds: json.beds,
  description: json.description,
  descriptionTranslations: json.descriptionTranslations?.map((translation) => ({
    language: translation.language,
    value: translation.value,
  })),
  id: json.id,
  images: json.images?.map(toEmbeddedFile),
  leaseTerm: toLeaseTerm(json.leaseTerm),
  listed: toDate(json.listed),
  locationAnalysis: json.locationAnalysis,
  petPolicy: json.petPolicy && toPetPolicy(json.petPolicy),
  pinned: json.pinned,
  primaryImage: json.primaryImage && toEmbeddedFile(json.primaryImage),
  primaryVideo: json.primaryVideo && toEmbeddedFile(json.primaryVideo),
  propertyType: toPropertyType(json.propertyType),
  rentalPrice: json.rentalPrice,
  securityDeposit: json.securityDeposit,
  singleLineAddress: json.singleLineAddress,
  tags: json.tags,
  terms: json.terms,
  title: json.title,
  unitSizeSqft: json.unitSizeSqft,
  updated: toDate(json.updated),
  videos: json.videos?.map(toEmbeddedFile),
});

class CompanyUnitListingGateway extends Gateway {
  public async getCompanyUnitsListing(params: UnitParams, companyId?: string): Promise<PaginatedUnits> {
    const request: MagicRequest = {
      url: this.urlWithQuery(COMPANY_UNITS_LISTING_URL, { ...params }),
      method: RequestMethod.GET,
      headers: this.createCommonHeaders(),
      companyId,
    };
    const response = await this.sendRequest(request);
    const json: PaginatedUnitListingJson = await response.json();
    return {
      currentPage: json.currentPage,
      items: (json.items ?? []).map(toCompanyUnit),
      pageSize: json.pageSize,
      totalCount: json.totalCount,
      totalPages: json.totalPages,
    };
  }

  public async getUnitListingDetail(unitId: string, companyId?: string): Promise<TenantUnitListing> {
    const request: MagicRequest = {
      url: `${COMPANY_UNITS_LISTING_URL}/${unitId}`,
      method: RequestMethod.GET,
      headers: this.createCommonHeaders(),
      companyId,
    };
    const response = await this.sendRequest(request);
    const json: UnitListingDetailJson = await response.json();
    return toUnitListing(json);
  }
}

export const companyUnitListingGateway = new CompanyUnitListingGateway(fullStackNetworkManager);
